/**
 * WHERE a delimited span ENDS — the scanners the SQL masker runs once it has
 * decided that a character opens one.
 *
 * The three modules of the masker split by question:
 *   - `sqlDelimiterPolicy.ts` — WHICH character opens what, per engine (the
 *     table).
 *   - this module — where the span it opened ends (doubled closers, backslash
 *     escapes, dollar tags, Oracle quote delimiters).
 *   - `sqlTextMasker.ts` — the RULE that walks the text and blanks what these
 *     two agree is a string or a comment.
 *
 * Internal to the masker: `sqlTextMasker.ts` is the only importer, and it
 * re-exports nothing from here.
 */

/** Characters that may appear INSIDE a bare SQL identifier on some engine
 *  (`$` in PostgreSQL/MySQL names, `#` in T-SQL/Oracle ones). A `$`, `q` or
 *  `E` preceded by one of these continues that identifier and never opens a
 *  literal. */
export function isIdentifierChar(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9_$#]/.test(ch);
}

/**
 * Scans a delimited span opening at `text[start]` (the opening delimiter
 * itself) — a delimited identifier or a string literal, which share this
 * grammar — and returns the index just past its closing delimiter.
 *
 * A doubled closer (`''`, `""`, `]]`, `` `` ``) is the engine-standard escape
 * for a literal closer character inside the span and does not end it; for
 * identifiers this mirrors `readDelimitedPart` in
 * `navigational-features/index/ddlClassifier.ts`.
 *
 * `backslashEscapes` adds the OTHER escape some grammars give a STRING (never
 * a delimited identifier): a backslash escapes the next character, closer
 * included — MySQL's default lexing and PostgreSQL's `E'…'`. `\\` is one
 * escaped backslash, so the character after it is not itself an escape lead.
 * Without it, `'it\'s'` closes at the escaped apostrophe and the real closer
 * re-opens a span that swallows everything after it.
 *
 * Returns `text.length` when the span is unterminated, so the caller copies
 * (identifier) or masks (string) the remainder.
 */
export function delimitedSpanEnd(
  text: string,
  start: number,
  closer: string,
  backslashEscapes = false,
): number {
  let i = start + 1;
  while (i < text.length) {
    const ch = text[i];
    if (backslashEscapes && ch === '\\') {
      i += 2;
      continue;
    }
    if (ch === closer) {
      if (text[i + 1] === closer) {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i += 1;
  }
  return text.length;
}

/**
 * `true` when `text[start]` is the `E` of a PostgreSQL escape-string literal
 * (`E'…'` / `e'…'`) rather than a letter of the identifier before it — the
 * same word-boundary rule dollar quotes and Oracle's `q'…'` follow. The caller
 * has already checked its engine's table row.
 */
export function isEscapeStringLead(text: string, start: number): boolean {
  const ch = text[start];
  return (ch === 'E' || ch === 'e') && text[start + 1] === "'" && !isIdentifierChar(text[start - 1]);
}

/** A complete PostgreSQL dollar-quote opener: `$$` or `$tag$`, the tag being a
 *  bare identifier. Sticky so the scan never slices the input. */
const DOLLAR_TAG_RE = /\$([A-Za-z_][A-Za-z0-9_]*)?\$/y;

/**
 * PostgreSQL dollar-quoted string starting at `text[start]` (`$`): returns the
 * index just past the matching closing tag, or `null` when `start` does NOT
 * open one — `$1` (a positional parameter), a lone `$`, `$` followed by a tag
 * that never closes its opener. Only a COMPLETE `$tag$` opener starts a
 * literal, so an unterminated `$…` can never blank the rest of the statement;
 * an opener whose closing tag is missing does run to the end of `text`, which
 * is what PostgreSQL itself reads there.
 */
export function dollarQuotedSpanEnd(text: string, start: number): number | null {
  DOLLAR_TAG_RE.lastIndex = start;
  const opener = DOLLAR_TAG_RE.exec(text);
  if (!opener) return null;
  const tag = opener[0];
  const closeIdx = text.indexOf(tag, start + tag.length);
  return closeIdx < 0 ? text.length : closeIdx + tag.length;
}

/** The leading character of an Oracle quote-delimiter literal: `q'…'` or the
 *  national-charset `nq'…'`, either case. */
export const Q_QUOTE_LEAD = /[qQnN]/;
/** …and the opener it must be followed by. Sticky, like `DOLLAR_TAG_RE`. */
const Q_QUOTE_OPEN_RE = /[nN]?[qQ]'/y;
/** The four bracket pairs Oracle closes with the MIRROR character; every other
 *  delimiter closes with itself. */
const Q_QUOTE_CLOSERS: Readonly<Record<string, string>> = {
  '[': ']',
  '{': '}',
  '(': ')',
  '<': '>',
};

/**
 * Oracle `q'X…X'` literal starting at `text[start]`: returns the index just
 * past the closing `X'`, or `null` when `start` does not open one. The
 * delimiter `X` is the character right after `q'`; the four bracket pairs
 * close with their mirror, any other character with itself. An unterminated
 * literal runs to the end of `text`.
 */
export function qQuotedSpanEnd(text: string, start: number): number | null {
  Q_QUOTE_OPEN_RE.lastIndex = start;
  const opener = Q_QUOTE_OPEN_RE.exec(text);
  if (!opener) return null;
  const delimiterIdx = start + opener[0].length;
  const open = text[delimiterIdx];
  if (open === undefined) return null;
  const closer = `${Q_QUOTE_CLOSERS[open] ?? open}'`;
  const closeIdx = text.indexOf(closer, delimiterIdx + 1);
  return closeIdx < 0 ? text.length : closeIdx + closer.length;
}
